import { createElement, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useMutation } from '@apollo/client';
import { useRouter } from 'next/router';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { useCurrentUser } from '@/features/Profile/core/hooks';
import { UPDATE_USER } from '@/api/mutations/users.mutations';
import {
  UpdateUserResponse,
  UpdateUserVariables,
} from '@/api/types/user.types';
import { GET_BASE_USER, GET_USER } from '@/api/queries/user.queries';
import { MutationError } from '@/api/types/common.types';
import { useToastStore } from '@/store/useToastStore';
import { SuccessFilledIcon } from '@/components/UI/Icons/Icons';

const editProfileFormSchema = z.object({
  name: z.string(),
  email: z.string(),
});

type EditProfileFormFields = z.infer<typeof editProfileFormSchema>;

function getFormFieldFromErrorPath(
  path?: string[]
): keyof EditProfileFormFields | null {
  const fieldName = path?.at(-1);

  if (fieldName === 'name' || fieldName === 'email') {
    return fieldName;
  }

  return null;
}

export function useEditProfileSection() {
  const { t } = useTranslation();
  const router = useRouter();
  const { user } = useCurrentUser();
  const openToast = useToastStore(state => state.openToast);
  const [updateUser, { loading: isUpdating }] = useMutation<
    UpdateUserResponse,
    UpdateUserVariables
  >(UPDATE_USER, {
    refetchQueries: [GET_USER, GET_BASE_USER],
    awaitRefetchQueries: true,
  });
  const {
    control,
    handleSubmit,
    reset,
    setError,
    formState: { errors },
  } = useForm<EditProfileFormFields>({
    resolver: zodResolver(editProfileFormSchema),
    defaultValues: {
      name: user.name ?? '',
      email: user.email ?? '',
    },
  });

  useEffect(() => {
    reset(
      {
        name: user.name ?? '',
        email: user.email ?? '',
      },
      { keepDirtyValues: true }
    );
  }, [reset, user.email, user.name]);

  function setMutationErrors(mutationErrors: MutationError[]) {
    mutationErrors.forEach(error => {
      const fieldName = getFormFieldFromErrorPath(error.path);

      if (fieldName) {
        setError(fieldName, { message: error.message });
        return;
      }

      setError('root', { message: error.message });
    });
  }

  function showSaveSuccessToast() {
    openToast({
      text: t('common.changes_successfully_saved'),
      icon: createElement(SuccessFilledIcon, { width: 20, height: 20 }),
      size: 'compact',
      borderRadius: 14,
    });
  }

  async function saveProfile(data: EditProfileFormFields) {
    try {
      const response = await updateUser({
        variables: {
          userParams: {
            name: data.name,
            email: data.email,
          },
        },
      });

      const result = response.data?.updateUser;
      if (!result) {
        setError('root', { message: t('common.something_went_wrong') });
        return;
      }

      if (result.errors.length) {
        setMutationErrors(result.errors);
        return;
      }

      if (!result.entity) {
        setError('root', { message: t('common.something_went_wrong') });
        return;
      }

      showSaveSuccessToast();
      router.back();
    } catch {
      setError('root', { message: t('common.something_went_wrong') });
    }
  }

  return {
    control,
    errors,
    isUpdating,
    submitProfile: handleSubmit(saveProfile),
  };
}
